<template>
  <div>
    <el-dialog
      :title="title"
      :visible.sync="syncDialog"
      :close-on-click-modal="false"
      size="small"
      class="text-left integration-dialog"
      :close-on-press-escape="(enableCloseOnPressEscape && !isAnyRowLoading)"
      :show-close="(showClose && !isAnyRowLoading)"
      @close="fnClose"
      @opened="initSpreadsheet"
      ref="dialog"
    >
      <!-- ✅ Preloader overlay while fetching DB list OR fetching/hydrating invoice series -->
      <div
        class="integration-dialog-body"
        v-loading="isFetchOverlay"
        element-loading-text="Fetching data..."
      >
        <div class="dialog-body-header p-1">
          <el-radio
            v-model="databaseCheckedOptions"
            border
            style="border-color: #409EFF;"
            class="mr-1"
            v-for="item in getDb"
            :key="item.value"
            :label="item.value"
          >
            {{ item.name }}
          </el-radio>
        </div>

        <div ref="spreadsheet"></div>
      </div>

      <div slot="footer" class="dialog-footer">
        <mdb-btn @click="fnRun" color="elegant" size="sm" :disabled="isRunDisabled">
          Run
        </mdb-btn>

        <mdb-btn @click="fnClose" color="elegant" size="sm" :disabled="isAnyRowLoading">
          Close
        </mdb-btn>
      </div>
    </el-dialog>

    <!-- ✅ Batch assignment dialog -->
    <el-dialog
      title="Assign Batch"
      :visible.sync="batchDialog.visible"
      :close-on-click-modal="false"
      width="740px"
      append-to-body
    >
      <div v-loading="batchDialog.loading" element-loading-text="Loading batches...">
        <div class="batch-dialog-meta">
          <div><b>Series:</b> {{ batchDialog.series }}</div>
          <div><b>Item:</b> {{ batchDialog.item_code }}</div>
          <div><b>Warehouse:</b> {{ batchDialog.warehouse }}</div>
          <div class="mt-1">
            <b>Requested Qty:</b> {{ batchDialog.requestedQty }}
            <span class="ml-2"><b>OpenQty:</b> {{ batchDialog.openQty }}</span>
            <span class="ml-2"><b>Available:</b> {{ batchDialog.available }}</span>
            <span class="ml-2"><b>BaseEntry:</b> {{ batchDialog.baseEntry }}</span>
            <span class="ml-2"><b>BaseLine:</b> {{ batchDialog.baseLine }}</span>
          </div>
        </div>

        <div v-if="(batchDialog.batches || []).length === 0" class="batch-empty">
          No batches found for this item.
        </div>

        <el-table
          v-else
          :data="batchDialog.batches"
          border
          size="mini"
          style="width: 100%;"
          height="320"
        >
          <el-table-column prop="batch_number" label="Batch Number" min-width="180" />
          <el-table-column prop="expDate" label="Exp Date" min-width="160">
            <template slot-scope="scope">
              {{ scope.row.expDate || '-' }}
            </template>
          </el-table-column>

          <el-table-column prop="available" label="OnHand" min-width="110" align="right" />

          <el-table-column label="Assign Qty" min-width="170" align="right">
            <template slot-scope="scope">
              <number-field
                v-model="scope.row.assign_qty"
                :min="0"
                :max="Number(scope.row.available || 0)"
                :step="1"
                size="mini"
                style="width: 140px;"
              />
            </template>
          </el-table-column>
        </el-table>

        <div class="batch-dialog-summary mt-2">
          <div>
            <b>Total Assigned:</b> {{ batchTotalAssigned }}
            <span class="ml-2">
              <b>Remaining:</b>
              {{ Math.max(0, (Number(batchDialog.requestedQty)||0) - batchTotalAssigned) }}
            </span>
          </div>

          <div v-if="batchDialogError" class="batch-dialog-error mt-1">
            {{ batchDialogError }}
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <mdb-btn
          color="elegant"
          size="sm"
          :disabled="batchDialog.loading || !batchCanSave"
          @click="saveBatchAssignment"
        >
          Save
        </mdb-btn>
        <mdb-btn
          color="elegant"
          size="sm"
          :disabled="batchDialog.loading"
          @click="batchDialog.visible = false"
        >
          Cancel
        </mdb-btn>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import jspreadsheet from '../../jsexcel/jexcel'
import { mdbBtn } from 'mdbvue'
import { MessageBox } from 'element-ui'
import NumberField from './NumberField.vue'

export default {
  components: { mdbBtn, NumberField },

  data() {
    return {
      syncDialog: false,
      showClose: true,
      processing: false,
      rowLoading: {},
      spreadsheet: null,
      databaseCheckedOptions: '',
      enableCloseOnPressEscape: true,

      // ✅ highlight for invalid OpenQty rows
      OPEN_QTY_INVALID_BG: '#f4cccc',

      // Series ItemCode Qty DeliveryDate Batch Warehouse OpenQty Available Status Message
      // U_RefItemId(hidden) BaseEntry(hidden) BaseLine(hidden)
      COL: {
        SERIES: 0,
        ITEM_CODE: 1,
        QTY: 2,
        DELIVERY_DATE: 3,
        BATCH_NUMBER: 4,
        WAREHOUSE: 5,
        OPEN_QTY: 6,
        AVAILABLE: 7,
        STATUS: 8,
        MESSAGE: 9,
        U_REF_ITEM_ID: 10, // hidden
        BASE_ENTRY: 11,    // ✅ hidden (DocEntry)
        BASE_LINE: 12      // ✅ hidden (LineNum)
      },

      // ✅ unique series cache
      seriesCache: {},            // { cacheKey: invoiceRecord|null }
      seriesFetchPromises: {},    // { cacheKey: Promise }
      invoiceQueue: Promise.resolve(),

      suspendSheetEvents: false,
      hydrateTimer: null,

      // ✅ fetch/hydrate preloader state
      seriesHydrating: false,
      hydratePromise: null,
      hydrateAgain: false,

      invalidOpenQtyRows: {},

      // ✅ batch support (no new rows)
      rowBatchEligible: {},       // rowIndex1 => true if valid + batch-managed
      rowBatchOptions: {},        // rowIndex1 => [{batch_number, available, expDate}]
      rowBatchAssignments: {},    // rowIndex1 => [{batch_number, qty}]

      // ✅ click handler to open batch dialog in ONE click
      sheetClickHandler: null,

      // ✅ batch dialog state
      batchDialog: {
        visible: false,
        loading: false,
        rowIndex1: null,
        series: '',
        item_code: '',
        warehouse: '',
        requestedQty: 0,
        openQty: 0,
        available: 0,
        baseEntry: 0,
        baseLine: null,
        batches: []
      },

      spreadsheetOptions: {
        download: false,
        allowDeleteColumn: false,
        allowRenameColumn: false,
        defaultColAlign: 'left',
        allowInsertColumn: false,
        csvHeader: true,
        tableWidth: '100%',
        tableHeight: 'calc(100vh - 335px)',
        tableOverflow: true,
        data: [[]],

        // ✅ now 13 columns (10 visible + 3 hidden)
        minDimensions: [13, 50],

        selectionCopy: false,
        allowCopy: true,

        columns: [
          { type: 'text', title: 'Series', name: 'series', readOnly: false },
          { type: 'text', title: 'Item Code', name: 'item_code', readOnly: false },
          { type: 'numeric', title: 'Qty', name: 'qty', readOnly: false, mask: '#,##.00', decimal: '.' },
          { type: 'calendar', title: 'Delivery Date', name: 'delivery_date', readOnly: false, options: { format: 'YYYY-MM-DD' } },

          // ✅ Batch is clickable (styled like a button), but not manually editable
          { type: 'text', title: 'Batch', name: 'batch_number', readOnly: true },

          { type: 'text', title: 'Warehouse', name: 'warehouse', readOnly: true },
          { type: 'numeric', title: 'Open Qty', name: 'open_qty', readOnly: true },
          { type: 'numeric', title: 'Available', name: 'available', readOnly: true },
          { type: 'text', title: 'Status', name: 'status', readOnly: true },
          { type: 'text', title: 'Response', name: 'message', readOnly: true },

          // ✅ hidden columns
          { type: 'hidden', title: 'U_RefItemId', name: 'U_RefItemId', readOnly: true, hidden: true },
          { type: 'hidden', title: 'BaseEntry', name: 'BaseEntry', readOnly: true, hidden: true },
          { type: 'hidden', title: 'BaseLine', name: 'BaseLine', readOnly: true, hidden: true }
        ],

        contextMenu: function () {
          return false
        }
      }
    }
  },

  props: {
    visible: { type: Boolean, default: false },
    endpoint: { type: String, default: 'inventory-transfer' },
    group: { type: Number, default: 2 },
    defaultDatabase: { type: String, default: 'THATSLIVING' },
    title: { type: String, default: 'DATA INTEGRATION' },

    invoiceConnection: { type: String, default: 'QATL_PRODUCTION' },
    invoiceLocation: { type: [Number, String], default: 1 }
  },

  computed: {
    ...mapGetters('DataIntegrationModule', ['getDb', 'getFetchLoading']),

    isAnyRowLoading() {
      return Object.values(this.rowLoading).some((v) => v)
    },

    isSeriesFetching() {
      return this.seriesHydrating || Object.keys(this.seriesFetchPromises || {}).length > 0
    },

    isFetchOverlay() {
      return this.getFetchLoading || this.isSeriesFetching
    },

    isRunDisabled() {
      return this.getFetchLoading || this.isSeriesFetching || this.isAnyRowLoading || this.processing
    },

    batchTotalAssigned() {
      return (this.batchDialog.batches || [])
        .reduce((s, b) => s + (Number(b.assign_qty) || 0), 0)
    },

    batchDialogError() {
      if (this.batchDialog.loading) return ''

      const rq = Number(this.batchDialog.requestedQty) || 0
      const openQty = Number(this.batchDialog.openQty) || 0
      const sum = this.batchTotalAssigned

      if (rq <= 0) return 'Requested qty must be greater than zero.'
      if (rq > openQty) return `Requested qty (${rq}) exceeds OpenQty (${openQty}).`
      if (sum !== rq) return `Total assigned (${sum}) must equal requested qty (${rq}).`
      return ''
    },

    batchCanSave() {
      const rq = Number(this.batchDialog.requestedQty) || 0
      const openQty = Number(this.batchDialog.openQty) || 0
      const sum = this.batchTotalAssigned
      return rq > 0 && rq <= openQty && sum === rq && !this.batchDialogError
    }
  },

  watch: {
    visible(val) {
      this.syncDialog = val
      if (val) {
        this.fetchDb()
        this.databaseCheckedOptions = this.defaultDatabase
      }
    },

    invoiceConnection() {
      this._resetInvoiceCaches()
      this._debouncedHydrate()
    },

    invoiceLocation() {
      this._resetInvoiceCaches()
      this._debouncedHydrate()
    }
  },

  methods: {
    ...mapActions('DataIntegrationModule', ['fetchDb', 'integrate']),
    ...mapActions('InvoiceModule', ['getRecordSeriesNoCommit']),

    openDialog() {
      this.syncDialog = true
      this.fetchDb()
      this.databaseCheckedOptions = this.defaultDatabase
      // ❌ DO NOT call initSpreadsheet here (avoids double-init race)
    },

    fnClose() {
      this.syncDialog = false
      this.$emit('close')
    },

    async fnRun() {
      MessageBox.confirm(
        `Are you sure you want to run this integration? This will be push to ${this.databaseCheckedOptions}.`,
        'Confirmation',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning'
        }
      ).then(async () => {
        await this._hydrateRowsByUniqueSeries()
        this.fnPostData()
      })
    },

    // ---------------------------------------------------------
    // POST: now includes BaseEntry(Base doc entry) + BaseType=17
    // and each line includes BaseLine (order LineNum)
    // ---------------------------------------------------------
    fnPostData() {
      const self = this
      const tableData = this.spreadsheet?.getJson?.() || []
      if (!this.spreadsheet || tableData.length === 0 || this.processing) return

      const normalize = (v) => (v || '').toString().trim()

      const normalizeYMD = (v) => {
        const s = normalize(v)
        if (!s) return ''
        return s.length >= 10 ? s.substring(0, 10) : s
      }

      const toWarehouseCode = (v) => {
        const s = normalize(v)
        if (!s) return ''
        return /^\d+(\.\d+)?$/.test(s) ? Number(s) : s
      }

      const sumAssigned = (assigns) => {
        if (!Array.isArray(assigns)) return 0
        return assigns.reduce((s, a) => s + (Number(a?.qty) || 0), 0)
      }

      const inputRows = []
      const readyRows = []
      const skippedRows = []
      const missingBatchRows = []
      const missingBaseRows = []

      tableData.forEach((row, idx) => {
        const rowIndex1 = idx + 1

        const series = normalize(row.series)
        const itemCode = normalize(row.item_code)
        const qty = Number(row.qty || 0)
        const status = normalize(row.status)

        const hasInputs = series !== '' && itemCode !== '' && qty > 0
        if (!hasInputs) return

        inputRows.push({ rowIndex1, row })

        // ✅ batch-managed eligible: must have assignments matching qty
        if (self.rowBatchEligible[rowIndex1]) {
          const assigns = self.rowBatchAssignments[rowIndex1] || []
          const total = sumAssigned(assigns)
          if (!Array.isArray(assigns) || assigns.length === 0 || total !== qty) {
            missingBatchRows.push({ rowIndex1, series, itemCode })
          }
        }

        // ✅ BaseEntry/BaseLine required for Ready rows
        if (status === 'Ready') {
          const baseEntry = Number(row.BaseEntry || 0)
          const baseLine = Number(row.BaseLine)
          if (!baseEntry || !Number.isFinite(baseLine)) {
            missingBaseRows.push({ rowIndex1, series, itemCode })
          }
        }

        if (status === 'Ready') readyRows.push({ rowIndex1, row })
        else skippedRows.push({ rowIndex1, status })
      })

      if (inputRows.length === 0) {
        this.$ThatsNotify.error({ message: 'Please select at least one item', type: 'error' })
        return
      }

      if (missingBatchRows.length > 0) {
        const preview = missingBatchRows
          .slice(0, 8)
          .map(x => `Row ${x.rowIndex1} (${x.itemCode})`)
          .join(', ')

        this.$ThatsNotify.error({
          message: `Batch assignment is required before submit. Fix: ${preview}${missingBatchRows.length > 8 ? ' ...' : ''}`,
          type: 'error'
        })
        return
      }

      if (readyRows.length === 0) {
        this.$ThatsNotify.error({
          message: 'No rows are Ready. Fix validation errors and assign batches where required.',
          type: 'error'
        })
        return
      }

      if (missingBaseRows.length > 0) {
        const preview = missingBaseRows
          .slice(0, 8)
          .map(x => `Row ${x.rowIndex1} (${x.itemCode})`)
          .join(', ')

        this.$ThatsNotify.error({
          message: `Missing BaseEntry/BaseLine mapping for Ready rows. Fix: ${preview}${missingBaseRows.length > 8 ? ' ...' : ''}`,
          type: 'error'
        })
        return
      }

      if (skippedRows.length > 0) {
        const uniq = Array.from(new Set(skippedRows.map(r => r.status))).filter(Boolean)
        this.$ThatsNotify.warning({
          message: `${skippedRows.length} row(s) will be skipped: ${uniq.join(', ')}`,
          type: 'warning'
        })
      }

      const missingDate = readyRows.filter(({ row }) => !normalizeYMD(row.delivery_date))
      if (missingDate.length > 0) {
        const preview = missingDate.slice(0, 8).map(x => `Row ${x.rowIndex1}`).join(', ')
        this.$ThatsNotify.error({
          message: `Delivery Date is required for Ready rows. Fix: ${preview}${missingDate.length > 8 ? ' ...' : ''}`,
          type: 'error'
        })
        return
      }

      // ✅ Group by (series + delivery_date + BaseEntry) to guarantee correct base doc
      const groups = new Map()

      readyRows.forEach(({ rowIndex1, row }) => {
        const series = normalize(row.series)
        const docDate = normalizeYMD(row.delivery_date)
        const baseEntry = Number(row.BaseEntry || 0)

        const key = `${series}::${docDate}::${baseEntry}`

        if (!groups.has(key)) {
          groups.set(key, {
            series,
            docDate,
            baseEntry,
            rowIndexes: [],
            payload: {
              DocDate: docDate,
              DocDueDate: docDate,
              U_WinvoiceNum: series,

              // ✅ requested by you
              BaseType: 17,
              BaseEntry: baseEntry,

              DocumentLines: []
            }
          })
        }

        const g = groups.get(key)
        g.rowIndexes.push(rowIndex1)

        const itemCode = normalize(row.item_code)
        const qty = Number(row.qty || 0)
        const whCode = toWarehouseCode(row.warehouse)

        const baseLine = Number(row.BaseLine)
        const assigns = self.rowBatchAssignments[rowIndex1] || []
        const batchNumbers = Array.isArray(assigns)
          ? assigns.map(a => ({
            BatchNumber: normalize(a.batch_number),
            Quantity: Number(a.qty || 0)
          })).filter(b => b.BatchNumber && b.Quantity > 0)
          : []

        const refItemId = normalize(row.U_RefItemId)

        const line = {
          ItemCode: itemCode,
          Quantity: qty,
          WarehouseCode: whCode,

          BaseEntry: baseEntry,
          BaseLine: baseLine,

          BatchNumbers: batchNumbers
        }
        if (refItemId) line.U_RefItemId = refItemId

        g.payload.DocumentLines.push(line)
      })

      const groupedList = Array.from(groups.values())
      if (groupedList.length === 0) return

      this.processing = true
      let pending = groupedList.length

      groupedList.forEach((g) => {
        g.rowIndexes.forEach((rowIndex1) => {
          self._setCellByIndex(self.COL.STATUS, rowIndex1, 'Please wait...')
          self._setCellByIndex(self.COL.MESSAGE, rowIndex1, '')
          self.$set(self.rowLoading, rowIndex1, true)
        })

        self.integrate({
          endpoint: self.endpoint,
          connection: self.databaseCheckedOptions,

          DocDate: g.payload.DocDate,
          DocDueDate: g.payload.DocDueDate,
          U_WinvoiceNum: g.payload.U_WinvoiceNum,

          // ✅ new
          BaseType: g.payload.BaseType,
          BaseEntry: g.payload.BaseEntry,

          DocumentLines: g.payload.DocumentLines
        })
          .then((response) => {
            const msg = response?.message || 'Success'
            g.rowIndexes.forEach((rowIndex1) => {
              self._paintRow(rowIndex1, '#00c851')
              self._setCellByIndex(self.COL.STATUS, rowIndex1, 'Success...')
              self._setCellByIndex(self.COL.MESSAGE, rowIndex1, msg)
            })
          })
          .catch((error) => {
            const msg =
              error?.response?.data?.message ||
              error?.response?.data?.errors?.value ||
              'Failed...'

            g.rowIndexes.forEach((rowIndex1) => {
              self._paintRow(rowIndex1, '#fb3')
              self._setCellByIndex(self.COL.STATUS, rowIndex1, 'Failed...')
              self._setCellByIndex(self.COL.MESSAGE, rowIndex1, msg)
            })
          })
          .finally(() => {
            g.rowIndexes.forEach((rowIndex1) => {
              self.$set(self.rowLoading, rowIndex1, false)
            })

            pending--
            if (pending <= 0) self.processing = false
          })
      })

      if (pending === 0) this.processing = false
    },

    // -----------------------------
    // Spreadsheet init (await destroy)
    // -----------------------------
    async recreateSpreadsheet() {
      this._unbindSheetClick()

      if (this.spreadsheet) {
        try {
          await this.spreadsheet.destroy()
        } catch (e) {
          // ignore
        }
        this.spreadsheet = null
      }

      if (this.$refs.spreadsheet) {
        this.$refs.spreadsheet.innerHTML = ''
      }
    },

    async initSpreadsheet() {
      await this.recreateSpreadsheet()
      this.createPopupTable(this.spreadsheetOptions)
    },

    createPopupTable(jspreadsheetOptions) {
      const el = this.$refs.dialog?.$el
      const firstChild = el?.firstElementChild
      const width = firstChild?.offsetWidth || 1100
      const widthForTable = width - 70

      // weights for the 10 visible cols (hidden cols forced to 0px)
      const weights = [1.2, 1.6, 0.8, 1.1, 1.2, 1.0, 0.9, 0.9, 0.9, 1.8]
      const total = weights.reduce((a, b) => a + b, 0)
      const sections = weights.map((w) => (widthForTable * w) / total)

      const existingColumns = jspreadsheetOptions.columns
      jspreadsheetOptions.tableWidth = `${width}px`

      const isHiddenCol = (c) => {
        const t = (c?.type || '').toString().toLowerCase()
        return t === 'hidden' || c?.hidden === true
      }

      jspreadsheetOptions.columns = existingColumns.map((column, index) => {
        if (isHiddenCol(column)) {
          return { ...column, width: '0px', hidden: true }
        }
        return {
          ...column,
          width: `${Math.max(90, Math.floor(sections[index] || 120))}px`
        }
      })

      jspreadsheetOptions.onpaste = () => this._debouncedHydrate()
      jspreadsheetOptions.onafterchanges = () => this._debouncedHydrate()

      // ✅ rehydrate on edits (and clear assignment if row changes)
      jspreadsheetOptions.onchange = (instance, cell, col, row, value, oldValue) => {
        if (this.suspendSheetEvents) return

        const watched = [this.COL.SERIES, this.COL.ITEM_CODE, this.COL.QTY]
        if (!watched.includes(col)) return

        const v = (value ?? '').toString().trim()
        const o = (oldValue ?? '').toString().trim()
        if (v === o) return

        const rowIndex1 = row + 1
        this.$delete(this.rowBatchAssignments, rowIndex1)

        this._debouncedHydrate()
      }

      this.spreadsheet = jspreadsheet(this.$refs.spreadsheet, jspreadsheetOptions)
      Object.assign(this, this.spreadsheet)

      this.suspendSheetEvents = true
      try {
        this.spreadsheet.setData([])
      } finally {
        this.suspendSheetEvents = false
      }

      this._bindSheetClick()
    },

    _bindSheetClick() {
      if (!this.$refs.spreadsheet) return
      if (this.sheetClickHandler) return

      this.sheetClickHandler = (e) => {
        if (this.isSeriesFetching) return
        if (!this.spreadsheet) return

        const td = e.target && e.target.closest ? e.target.closest('td') : null
        if (!td) return

        const xRaw =
          td.getAttribute('data-x') ||
          td.getAttribute('data-col') ||
          td.getAttribute('data-column')

        const yRaw =
          td.getAttribute('data-y') ||
          td.getAttribute('data-row')

        let x = Number(xRaw)
        let y = Number(yRaw)

        if (!Number.isFinite(x)) x = td.cellIndex
        if (!Number.isFinite(y)) {
          const tr = td.parentElement
          const tbody = tr && tr.parentElement && tr.parentElement.tagName === 'TBODY'
            ? tr.parentElement
            : null
          if (tbody) y = Array.prototype.indexOf.call(tbody.children, tr)
        }

        if (!Number.isFinite(x) || !Number.isFinite(y)) return
        if (x !== this.COL.BATCH_NUMBER) return

        const rowIndex1 = y + 1
        if (!this.rowBatchEligible[rowIndex1]) return

        this.openBatchDialog(rowIndex1)
      }

      this.$refs.spreadsheet.addEventListener('click', this.sheetClickHandler, true)
    },

    _unbindSheetClick() {
      if (!this.$refs.spreadsheet) return
      if (!this.sheetClickHandler) return
      this.$refs.spreadsheet.removeEventListener('click', this.sheetClickHandler, true)
      this.sheetClickHandler = null
    },

    _resetInvoiceCaches() {
      this.seriesCache = {}
      this.seriesFetchPromises = {}
      this.invoiceQueue = Promise.resolve()

      this.invalidOpenQtyRows = {}
      this.rowBatchEligible = {}
      this.rowBatchOptions = {}
      this.rowBatchAssignments = {}
    },

    // -----------------------------
    // Hydration
    // -----------------------------
    _debouncedHydrate() {
      clearTimeout(this.hydrateTimer)
      this.hydrateTimer = setTimeout(() => {
        this._hydrateRowsByUniqueSeries()
      }, 350)
    },

    _normalize(v) {
      return (v || '').toString().trim()
    },

    _normalizeTreeType(v) {
      return this._normalize(v).toUpperCase()
    },

    _toNumber(v, fallback = 0) {
      const n = Number(v)
      return Number.isFinite(n) ? n : fallback
    },

    _isSalesItemTreeType(treeType) {
      const t = this._normalizeTreeType(treeType)
      return t === 'S'
    },

    _isInventoryTreeType(treeType) {
      const t = this._normalizeTreeType(treeType)
      return t === 'N' || t === 'I'
    },

    _cacheKey(series) {
      return `${this.invoiceConnection}::${this.invoiceLocation}::${series}`
    },

    async _hydrateRowsByUniqueSeries() {
      if (!this.spreadsheet) return
      if (this.suspendSheetEvents) return

      if (this.hydratePromise) {
        this.hydrateAgain = true
        return this.hydratePromise
      }

      this.seriesHydrating = true

      this.hydratePromise = (async () => {
        const tableData = this.spreadsheet.getJson() || []
        if (tableData.length === 0) return

        const uniqueSeries = Array.from(
          new Set(
            tableData
              .map((r) => this._normalize(r.series))
              .filter((s) => s !== '')
          )
        )

        for (const series of uniqueSeries) {
          try {
            await this._ensureSeriesLoaded(series)
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Failed to load series:', series, e)
          }
        }

        const updated = this._applyMatching(tableData)

        this.suspendSheetEvents = true
        try {
          this.spreadsheet.setData(updated)
        } finally {
          this.suspendSheetEvents = false
        }

        this._applyOpenQtyInvalidStyles(updated)
        this._applyBatchCellStyles(updated)
      })()
        .finally(() => {
          this.seriesHydrating = false
          this.hydratePromise = null

          if (this.hydrateAgain) {
            this.hydrateAgain = false
            setTimeout(() => this._hydrateRowsByUniqueSeries(), 0)
          }
        })

      return this.hydratePromise
    },

    _ensureSeriesLoaded(series) {
      const key = this._cacheKey(series)

      if (this.seriesCache[key] !== undefined) return Promise.resolve(this.seriesCache[key])
      if (this.seriesFetchPromises[key]) return this.seriesFetchPromises[key]

      const p = (this.invoiceQueue = this.invoiceQueue
        .then(() => {
          return this.getRecordSeriesNoCommit({
            series,
            connection: this.invoiceConnection,
            location: this.invoiceLocation
          })
        })
        .then((record) => {
          this.$set(this.seriesCache, key, record)
          return record
        })
        .catch((err) => {
          this.$set(this.seriesCache, key, null)
          throw err
        })
        .finally(() => {
          this.$delete(this.seriesFetchPromises, key)
        }))

      this.$set(this.seriesFetchPromises, key, p)
      return p
    },

    // -----------------------------
    // Extract + Bundle builder (BaseEntry + BaseLine + OpenQty + Batches)
    // -----------------------------
    _extractLines(invoiceRecord) {
      const docLines =
        invoiceRecord?.ordr_sap_data?.DocumentLines ||
        invoiceRecord?.DocumentLines

      if (Array.isArray(docLines) && docLines.length > 0) {
        const flat = []
        docLines.forEach((dl) => {
          flat.push(dl)
          const comps = dl?.components || dl?.Components
          if (Array.isArray(comps) && comps.length > 0) comps.forEach((c) => flat.push(c))
        })
        return flat
      }
      return []
    },

    _openQtyFromLine(dl) {
      const openQtyRaw =
        dl?.OpenQty ??
        dl?.OpenQuantity ??
        dl?.RemainingOpenQuantity ??
        dl?.RemainingOpenQty ??
        0
      return this._toNumber(openQtyRaw, 0)
    },

    _lineAvailableFallback(dl, openQtyFallback) {
      const raw = dl?.Available ?? dl?.available ?? dl?.OnHand ?? dl?.OnHandQty ?? null
      if (raw === null || raw === undefined) return openQtyFallback
      return this._toNumber(raw, 0)
    },

    _buildBundle(lines, invoiceRecord) {
      const meta = {}          // item => { hasSales, hasInventory, types:Set }
      const linePools = {}     // lineKey => { remaining, original }
      const linesByItem = {}   // item => [{ item, wh, stockKey, lineKey, lineNum, refId }]
      const linesByRefId = {}  // refId => [{ item, wh, stockKey, lineKey, lineNum }]
      const refIdByItem = {}   // item => refId fallback
      const refIdByLineKey = {} // lineKey => refId

      // ✅ stock per item::wh (dedup by taking MAX per batch, not SUM)
      const batchMaps = {}     // stockKey => { bn => { available, expDate } }
      const hasAnyBatch = {}   // stockKey => true if any bn != ''

      // ✅ BaseEntry (DocEntry)
      const baseEntry =
        this._toNumber(invoiceRecord?.ordr_sap_data?.DocEntry, 0) ||
        this._toNumber(invoiceRecord?.DocEntry, 0) ||
        this._toNumber(lines?.[0]?.DocEntry, 0) ||
        0

      const getRefId = (dl) => this._normalize(
        dl?.U_RefItemId ??
        dl?.U_RefItemID ??
        dl?.u_ref_item_id ??
        dl?.URefItemId ??
        ''
      )

      const ensureMeta = (item) => {
        if (!meta[item]) meta[item] = { hasSales: false, hasInventory: false, types: new Set() }
        return meta[item]
      }

      const ensureBatchMap = (stockKey) => {
        if (!batchMaps[stockKey]) batchMaps[stockKey] = {}
        return batchMaps[stockKey]
      }

      const ensureLinesByItem = (item) => {
        if (!linesByItem[item]) linesByItem[item] = []
        return linesByItem[item]
      }

      lines.forEach((dl) => {
        const item = this._normalize(dl?.ItemCode || dl?.item_code)
        if (!item) return

        const treeType = this._normalizeTreeType(dl?.TreeType)
        const m = ensureMeta(item)
        m.types.add(treeType || '')
        if (this._isSalesItemTreeType(treeType)) m.hasSales = true
        if (this._isInventoryTreeType(treeType)) m.hasInventory = true

        const wh = this._normalize(dl?.WhsCode || dl?.WarehouseCode || dl?.warehouse || '')
        const lineNum = this._toNumber(dl?.LineNum, -1)

        const refId = getRefId(dl)
        if (refId && !refIdByItem[item]) refIdByItem[item] = refId

        // only inventory lines participate in delivery mapping
        if (!this._isInventoryTreeType(treeType)) return
        if (!wh || lineNum < 0) return

        const stockKey = `${item}::${wh}`
        const lineKey = `${stockKey}::${lineNum}`

        const openQty = this._openQtyFromLine(dl)
        if (!linePools[lineKey]) linePools[lineKey] = { remaining: 0, original: openQty }
        linePools[lineKey].remaining += openQty

        // index for picking
        ensureLinesByItem(item).push({ item, wh, stockKey, lineKey, lineNum, refId })

        if (refId) {
          refIdByLineKey[lineKey] = refId
          if (!linesByRefId[refId]) linesByRefId[refId] = []
          linesByRefId[refId].push({ item, wh, stockKey, lineKey, lineNum })
        }

        // ✅ build stock map per item::wh (dedup with MAX)
        const batchMap = ensureBatchMap(stockKey)
        const batches = Array.isArray(dl?.BatchNumbers) ? dl.BatchNumbers : []

        if (batches.length > 0) {
          batches.forEach((b) => {
            const bn = this._normalize(b?.BatchNumber || b?.batchNumber || b?.number || '')
            if (!bn) return

            const onHand = this._toNumber(b?.OnHand ?? b?.available ?? 0, 0)
            const expDate = b?.ExpDate || b?.expDate || null

            if (!batchMap[bn]) batchMap[bn] = { available: onHand, expDate: expDate }
            else {
              batchMap[bn].available = Math.max(this._toNumber(batchMap[bn].available, 0), onHand)
              batchMap[bn].expDate = batchMap[bn].expDate || expDate
            }

            hasAnyBatch[stockKey] = true
          })
        } else {
          // non-batch managed: pseudo bucket ""
          const fallbackAvail = this._lineAvailableFallback(dl, openQty)
          if (!batchMap['']) batchMap[''] = { available: fallbackAvail, expDate: null }
          else batchMap[''].available = Math.max(this._toNumber(batchMap[''].available, 0), fallbackAvail)
        }
      })

      Object.keys(meta).forEach(k => { meta[k].types = Array.from(meta[k].types) })

      return {
        baseEntry,
        meta,
        linePools,
        linesByItem,
        linesByRefId,
        refIdByItem,
        refIdByLineKey,
        batchMaps,
        hasAnyBatch
      }
    },

    _sumAvailable(batchMap) {
      return Object.values(batchMap || {}).reduce(
        (s, b) => s + Math.max(0, this._toNumber(b.available, 0)),
        0
      )
    },

    _buildBatchOptions(bundle, stockKey) {
      const map = bundle?.batchMaps?.[stockKey] || {}
      return Object.keys(map)
        .filter(bn => this._normalize(bn) !== '')
        .map(bn => ({
          batch_number: bn,
          available: this._toNumber(map[bn]?.available, 0),
          expDate: map[bn]?.expDate || null
        }))
    },

    _validateAssignments(options, assigns, requestedQty) {
      const rq = this._toNumber(requestedQty, 0)
      if (!rq || rq <= 0) return { ok: false, reason: 'Invalid qty' }
      if (!Array.isArray(assigns) || assigns.length === 0) return { ok: false, reason: 'No assignment' }

      const optMap = {}
      options.forEach(o => { optMap[this._normalize(o.batch_number)] = o })

      let sum = 0
      for (const a of assigns) {
        const bn = this._normalize(a.batch_number)
        const q = this._toNumber(a.qty, 0)
        if (!bn) return { ok: false, reason: 'Missing batch number' }
        if (q <= 0) return { ok: false, reason: 'Invalid assigned qty' }
        const opt = optMap[bn]
        if (!opt) return { ok: false, reason: `Batch not found: ${bn}` }
        if (q > this._toNumber(opt.available, 0)) return { ok: false, reason: `Batch ${bn} exceeds OnHand` }
        sum += q
      }
      if (sum !== rq) return { ok: false, reason: `Sum ${sum} != requested ${rq}` }
      return { ok: true }
    },

    _formatBatchSummary(assigns) {
      if (!Array.isArray(assigns) || assigns.length === 0) return ''
      return assigns.map(a => `${this._normalize(a.batch_number)}:${this._toNumber(a.qty, 0)}`).join('; ')
    },

    _consumeAssignmentsInStock(bundle, stockKey, assigns) {
      const batchMap = bundle?.batchMaps?.[stockKey]
      if (!batchMap) return

      assigns.forEach(a => {
        const bn = this._normalize(a.batch_number)
        const q = this._toNumber(a.qty, 0)
        if (!batchMap[bn]) return
        batchMap[bn].available = this._toNumber(batchMap[bn].available, 0) - q
      })
    },

    _pickBestOrderLine(bundle, itemCode, qty, preferRefId) {
      const item = this._normalize(itemCode)
      const rq = this._toNumber(qty, 0)
      if (!item || rq <= 0) return null

      const ref = this._normalize(preferRefId)

      let candidates = []
      if (ref && bundle?.linesByRefId?.[ref]) {
        // enforce refId match (helps duplicate item codes)
        candidates = (bundle.linesByRefId[ref] || []).filter(x => x.item === item)
      } else {
        candidates = (bundle?.linesByItem?.[item] || [])
      }

      if (!Array.isArray(candidates) || candidates.length === 0) return null

      const evaluated = candidates.map((c) => {
        const open = this._toNumber(bundle?.linePools?.[c.lineKey]?.remaining, 0)
        const stock = this._sumAvailable(bundle?.batchMaps?.[c.stockKey] || {})
        const deliverable = Math.max(0, Math.min(open, stock))
        return { ...c, open, stock, deliverable }
      })

      const can = evaluated
        .filter(x => x.open >= rq && x.stock >= rq)
        .sort((a, b) => (a.open - b.open) || (a.stock - b.stock))

      if (can.length > 0) return can[0]

      // fallback: best deliverable for better error messages / display
      evaluated.sort((a, b) => b.deliverable - a.deliverable)
      return evaluated[0]
    },

    // -----------------------------
    // Matching: now sets BaseEntry + BaseLine (no auto new rows)
    // -----------------------------
    _applyMatching(tableData) {
      const bundleBySeries = {}
      const nextEligible = {}
      const nextOptions = {}

      const getBundle = (series) => {
        if (Object.prototype.hasOwnProperty.call(bundleBySeries, series)) return bundleBySeries[series]
        const key = this._cacheKey(series)
        const record = this.seriesCache[key]
        if (!record) {
          bundleBySeries[series] = null
          return null
        }
        bundleBySeries[series] = this._buildBundle(this._extractLines(record), record)
        return bundleBySeries[series]
      }

      const out = tableData.map((row, idx) => {
        const rowIndex1 = idx + 1
        nextEligible[rowIndex1] = false
        nextOptions[rowIndex1] = []

        const series = this._normalize(row.series)
        const itemCode = this._normalize(row.item_code)
        const qty = this._toNumber(row.qty || 0, 0)

        // keep empty rows
        if (!series && !itemCode && !qty) return { ...row }

        const o = {
          ...row,
          warehouse: row.warehouse || '',
          open_qty: row.open_qty || '',
          available: row.available || '',
          status: row.status || '',
          message: row.message || '',
          batch_number: row.batch_number || '',

          // ✅ always recompute hidden
          U_RefItemId: this._normalize(row.U_RefItemId || ''),
          BaseEntry: '',
          BaseLine: ''
        }

        if (!series) {
          o.status = 'Missing series'
          o.message = 'Please enter Series.'
          o.warehouse = ''
          o.open_qty = ''
          o.available = ''
          o.batch_number = ''
          return o
        }

        const bundle = getBundle(series)
        if (!bundle) {
          o.status = 'Series not found'
          o.message = 'Could not load invoice for this series.'
          o.warehouse = ''
          o.open_qty = ''
          o.available = ''
          o.batch_number = ''
          return o
        }

        // ✅ BaseEntry always available once bundle exists
        o.BaseEntry = this._toNumber(bundle.baseEntry, 0) || ''

        if (!itemCode) {
          o.status = 'Missing item'
          o.message = 'Please enter Item Code.'
          o.warehouse = ''
          o.open_qty = ''
          o.available = ''
          o.batch_number = ''
          return o
        }

        if (!qty || qty <= 0) {
          o.status = 'Invalid qty'
          o.message = 'Qty must be greater than zero.'
          o.warehouse = ''
          o.open_qty = ''
          o.available = ''
          o.batch_number = ''
          return o
        }

        const meta = bundle.meta?.[itemCode]
        if (meta && meta.hasSales && !meta.hasInventory) {
          o.status = 'Sales item not allowed'
          o.message = `Item is Sales/BOM parent (TreeType: ${meta.types.join(', ')}). Use inventory items only.`
          o.warehouse = ''
          o.open_qty = ''
          o.available = ''
          o.batch_number = ''
          // keep U_RefItemId fallback if we have it
          o.U_RefItemId = o.U_RefItemId || (bundle?.refIdByItem?.[itemCode] || '')
          return o
        }
        if (meta && !meta.hasInventory) {
          o.status = 'Not an inventory item'
          o.message = `Item is not an inventory line (TreeType: ${meta.types.join(', ')}).`
          o.warehouse = ''
          o.open_qty = ''
          o.available = ''
          o.batch_number = ''
          o.U_RefItemId = o.U_RefItemId || (bundle?.refIdByItem?.[itemCode] || '')
          return o
        }

        // ✅ pick ONE order line (BaseLine) that can satisfy qty (no auto-split)
        const preferredRefId = this._normalize(o.U_RefItemId)
        const pick = this._pickBestOrderLine(bundle, itemCode, qty, preferredRefId)

        if (!pick || !pick.wh) {
          o.status = 'Item not found'
          o.message = 'Item code not found in invoice lines.'
          o.warehouse = ''
          o.open_qty = ''
          o.available = ''
          o.batch_number = ''
          o.U_RefItemId = o.U_RefItemId || (bundle?.refIdByItem?.[itemCode] || '')
          return o
        }

        o.warehouse = pick.wh
        o.open_qty = pick.open
        o.available = pick.stock
        o.BaseLine = Number.isFinite(pick.lineNum) ? pick.lineNum : ''

        // ✅ keep row refId if pasted; else fill from chosen line if available
        o.U_RefItemId = preferredRefId || (pick.refId || '') || (bundle?.refIdByItem?.[itemCode] || '')

        if (pick.open <= 0) {
          o.status = 'Open qty is zero'
          o.message = 'OpenQty is 0. Delivery not allowed for this item.'
          o.batch_number = ''
          return o
        }

        if (qty > pick.open) {
          o.status = 'Qty exceeds open qty'
          o.message = `Requested ${qty}, but OpenQty (for BaseLine ${pick.lineNum}) is ${pick.open}. Split into multiple rows if needed.`
          o.batch_number = ''
          return o
        }

        if (pick.stock <= 0) {
          o.status = 'No available stock'
          o.message = 'No available stock for this item.'
          o.batch_number = ''
          return o
        }

        if (qty > pick.stock) {
          o.status = 'No available stock'
          o.message = `Requested ${qty}, but only ${pick.stock} available.`
          o.batch_number = ''
          return o
        }

        // ✅ reserve OpenQty immediately for this row (even if still "Assign batch")
        if (bundle?.linePools?.[pick.lineKey]) {
          bundle.linePools[pick.lineKey].remaining = this._toNumber(bundle.linePools[pick.lineKey].remaining, 0) - qty
        }

        const batchManaged = !!bundle.hasAnyBatch?.[pick.stockKey]

        if (!batchManaged) {
          // ✅ ready without batch selection
          o.status = 'Ready'
          o.message = ''
          o.batch_number = ''

          // reserve pseudo stock (dedup map uses "" bucket)
          const bm = bundle.batchMaps[pick.stockKey]
          if (bm && bm['']) bm[''].available = this._toNumber(bm[''].available, 0) - qty

          return o
        }

        // ✅ batch-managed => eligible (button/clickable)
        const options = this._buildBatchOptions(bundle, pick.stockKey)
        nextEligible[rowIndex1] = true
        nextOptions[rowIndex1] = options

        const assigns = this.rowBatchAssignments[rowIndex1] || []
        const valid = this._validateAssignments(options, assigns, qty)

        if (!valid.ok) {
          o.status = 'Assign batch'
          o.message = 'Click Batch cell to select batch quantities that equal the requested qty.'
          o.batch_number = 'Assign batch'
          return o
        }

        // ✅ ready with assigned batches
        o.status = 'Ready'
        o.message = ''
        o.batch_number = this._formatBatchSummary(assigns)

        // reserve batch stock only when assignments are valid
        this._consumeAssignmentsInStock(bundle, pick.stockKey, assigns)

        return o
      })

      this.rowBatchEligible = nextEligible
      this.rowBatchOptions = nextOptions

      return out
    },

    // -----------------------------
    // Batch dialog open/save
    // -----------------------------
    async openBatchDialog(rowIndex1) {
      if (!this.rowBatchEligible[rowIndex1]) return

      this.batchDialog.visible = true
      this.batchDialog.loading = true
      this.batchDialog.rowIndex1 = rowIndex1

      // ensure latest data
      await this._hydrateRowsByUniqueSeries()

      const tableData = this.spreadsheet?.getJson?.() || []
      const row = tableData[rowIndex1 - 1] || {}

      const options = this.rowBatchOptions[rowIndex1] || []
      const existing = this.rowBatchAssignments[rowIndex1] || []
      const existingMap = {}
      existing.forEach(a => { existingMap[this._normalize(a.batch_number)] = this._toNumber(a.qty, 0) })

      this.batchDialog.series = this._normalize(row.series)
      this.batchDialog.item_code = this._normalize(row.item_code)
      this.batchDialog.warehouse = this._normalize(row.warehouse)
      this.batchDialog.requestedQty = this._toNumber(row.qty, 0)
      this.batchDialog.openQty = this._toNumber(row.open_qty, 0)
      this.batchDialog.available = this._toNumber(row.available, 0)
      this.batchDialog.baseEntry = this._toNumber(row.BaseEntry, 0)
      this.batchDialog.baseLine = Number.isFinite(Number(row.BaseLine)) ? Number(row.BaseLine) : null

      this.batchDialog.batches = options.map(o => ({
        batch_number: o.batch_number,
        available: this._toNumber(o.available, 0),
        expDate: o.expDate || null,
        assign_qty: existingMap[this._normalize(o.batch_number)] || 0
      }))

      this.batchDialog.loading = false
    },

    saveBatchAssignment() {
      const rowIndex1 = this.batchDialog.rowIndex1
      if (!rowIndex1) return

      const requestedQty = this._toNumber(this.batchDialog.requestedQty, 0)
      const openQty = this._toNumber(this.batchDialog.openQty, 0)
      if (requestedQty <= 0 || requestedQty > openQty) return

      const assigns = (this.batchDialog.batches || [])
        .map(b => ({
          batch_number: this._normalize(b.batch_number),
          qty: this._toNumber(b.assign_qty, 0),
          available: this._toNumber(b.available, 0)
        }))
        .filter(x => x.qty > 0)

      const sum = assigns.reduce((s, a) => s + a.qty, 0)
      if (sum !== requestedQty) return

      for (const a of assigns) {
        if (a.qty > a.available) {
          this.$ThatsNotify.error({
            message: `Batch ${a.batch_number} exceeds OnHand.`,
            type: 'error'
          })
          return
        }
      }

      this.$set(this.rowBatchAssignments, rowIndex1, assigns.map(a => ({
        batch_number: a.batch_number,
        qty: a.qty
      })))

      this.batchDialog.visible = false
      this._hydrateRowsByUniqueSeries()
    },

    // -----------------------------
    // Styling
    // -----------------------------
    _applyOpenQtyInvalidStyles(tableData) {
      if (!this.spreadsheet) return

      const next = {}

      tableData.forEach((row, idx) => {
        const rowIndex1 = idx + 1
        const status = (row.status || '').toString().trim()

        if (status === 'Open qty is zero') {
          next[rowIndex1] = true
          this._paintRow(rowIndex1, this.OPEN_QTY_INVALID_BG)
        } else {
          if (this.invalidOpenQtyRows[rowIndex1]) this._clearRowBackground(rowIndex1)
        }
      })

      Object.keys(this.invalidOpenQtyRows).forEach((k) => {
        const rk = Number(k)
        if (!next[rk]) this._clearRowBackground(rk)
      })

      this.invalidOpenQtyRows = next
    },

    _applyBatchCellStyles(tableData) {
      if (!this.spreadsheet) return

      tableData.forEach((row, idx) => {
        const rowIndex1 = idx + 1
        const eligible = !!this.rowBatchEligible[rowIndex1]
        this._styleBatchCell(rowIndex1, eligible)
      })
    },

    _styleBatchCell(rowIndex1, enabled) {
      const ref = this._cellRef(this.COL.BATCH_NUMBER, rowIndex1)
      const styles = [
        ['background-color', enabled ? '#409EFF' : ''],
        ['color', enabled ? '#fff' : ''],
        ['font-weight', enabled ? '600' : ''],
        ['text-align', enabled ? 'center' : ''],
        ['cursor', enabled ? 'pointer' : '']
      ]
      styles.forEach(([k, v]) => {
        try {
          this.spreadsheet.setStyle(ref, k, v, true)
        } catch (e) {
          // ignore
        }
      })
    },

    // -----------------------------
    // Sheet helpers
    // -----------------------------
    _colToLetter(n) {
      let s = ''
      let x = n + 1
      while (x > 0) {
        const m = (x - 1) % 26
        s = String.fromCharCode(65 + m) + s
        x = Math.floor((x - 1) / 26)
      }
      return s
    },

    _cellRef(colIndex0, rowIndex1) {
      return `${this._colToLetter(colIndex0)}${rowIndex1}`
    },

    _setCellByIndex(colIndex0, rowIndex1, value) {
      const ref = this._cellRef(colIndex0, rowIndex1)
      this.spreadsheet.setValue(ref, value, true)
    },

    _paintRow(rowIndex1, color) {
      // ✅ keep to MESSAGE only (avoid styling hidden cols)
      for (let c = 0; c <= this.COL.MESSAGE; c++) {
        const ref = this._cellRef(c, rowIndex1)
        this.spreadsheet.setStyle(ref, 'background-color', color, true)
      }
    },

    _clearRowBackground(rowIndex1) {
      // ✅ keep to MESSAGE only (avoid styling hidden cols)
      for (let c = 0; c <= this.COL.MESSAGE; c++) {
        const ref = this._cellRef(c, rowIndex1)
        this.spreadsheet.setStyle(ref, 'background-color', '', true)
      }
    }
  }
}
</script>

<style scoped>
.select-location-item {
  text-transform: uppercase;
  font-size: 12px;
}

.batch-dialog-meta {
  font-size: 12px;
  margin-bottom: 10px;
}
.batch-dialog-error {
  color: #d9534f;
  font-size: 12px;
}
.batch-empty {
  font-size: 12px;
  padding: 10px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
}
</style>
